import { ReactElement, useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { Button, Typography, Space, Flex, Switch, Input, InputNumber, Select, Spin, Checkbox, Divider, Tooltip } from 'antd'
import { PiMapPin, PiTrash, PiInfo } from 'react-icons/pi'
import { useIntl } from 'react-intl'
import { useManualQuery } from 'graphql-hooks'
import { getWizardLeaveTypeData } from '../../graphql/custom-queries'
import { IOnboardingLeaveType, IOnboardingLocation, ILeaveTypeOverride, LEAVE_TYPE_COLORS, INTERVAL_TO_I18N_KEY, INTERVAL_ENUM_VALUES, normalizeIntervalEnum } from './types'
import { matchToApiSuggestion, getApiSuggestionsForLocations } from './leave-type-mapping'
import { useLeaveTypesApi } from '../../services/api/leave-type-templates-hook'
import IntlMessages from '../../util/IntlMessages'
import { FrontendUrls } from '../../types/urls'

const { Text } = Typography
const { Option } = Select

interface WizardLeaveTypeApiResult {
  getLeaveTypeList: Array<{ id: string; name: string; isActive: boolean; color: string; isDefault: boolean }>
  getLocation: {
    id: string
    leavePolicies: Array<{
      id: string
      daysPerYear: number
      hasUnlimitedDays: boolean
      isApprovalRequired: boolean
      allowHalfDays: boolean
      shortestLeaveInterval: string
      leaveType: { id: string }
    }>
  }
}

interface LeaveTypesStepProps {
  leaveTypes: IOnboardingLeaveType[]
  leaveTypesInitialized: boolean
  signupLocationId: string
  locations: IOnboardingLocation[]
  leaveTypeLimit: number
  minLeaveDuration: string
  onUpdate: (leaveTypes: IOnboardingLeaveType[]) => void
  onInitialized: (leaveTypes: IOnboardingLeaveType[]) => void
  onConfirm: () => void
  onSkip: () => void
  onNext?: () => void
  readOnly?: boolean
  planLimitExceeded?: boolean
}

interface OverrideFormProps {
  lt: IOnboardingLeaveType
  locationId: string
  minLeaveDuration: string
  onSave: (o: ILeaveTypeOverride) => void
  onCancel: () => void
  readOnly?: boolean
}

interface AddLeaveTypeFormProps {
  existingCount: number
  existingNames: string[]
  locations: IOnboardingLocation[]
  minLeaveDuration: string
  onAdd: (lt: IOnboardingLeaveType) => void
  onCancel: () => void
}

interface LeaveTypeRowProps {
  lt: IOnboardingLeaveType
  locations: IOnboardingLocation[]
  minLeaveDuration: string
  onChange: (updated: IOnboardingLeaveType) => void
  onDelete: (id: string) => void
  readOnly?: boolean
  locationsReadOnly?: boolean
  isExpanded: boolean
  onExpand: (id: string) => void
}

interface EditState {
  name: string
  color: string
  hasUnlimitedDays: boolean
  daysPerYear: number | null
  isApprovalRequired: boolean
  shortestLeaveIntervalEnum: string
}

const getAllowedIntervals = (minDuration: string): string[] => {
  const normalized = normalizeIntervalEnum(minDuration)
  const idx = INTERVAL_ENUM_VALUES.indexOf(normalized as (typeof INTERVAL_ENUM_VALUES)[number])
  if (idx === -1) return [...INTERVAL_ENUM_VALUES]
  return [...INTERVAL_ENUM_VALUES].slice(0, idx + 1)
}

const DEFAULT_NAME_MAP: Record<string, string> = {
  PTO: 'Paid Time Off',
  'Paid Time Off': 'Paid Time Off',
  'Sick Day': 'Sick Leave',
  'Sick Leave': 'Sick Leave',
  'Working Remotely': 'Working Remotely',
}

const isPTOName = (name: string) => name.toLowerCase().includes('paid time off')
const isSickLeaveName = (name: string) => name.toLowerCase().includes('sick')

const policyLabel = (lt: IOnboardingLeaveType, override?: ILeaveTypeOverride): string => {
  const src = override ?? lt
  const days = src.hasUnlimitedDays ? '∞' : `${src.daysPerYear}d`
  const approval = src.isApprovalRequired ? 'approval' : 'auto'
  return `${days}, ${approval}`
}

const isOverrideCustomized = (lt: IOnboardingLeaveType, override: ILeaveTypeOverride): boolean => {
  if (override.hasUnlimitedDays !== lt.hasUnlimitedDays) return true
  if (!lt.hasUnlimitedDays && override.daysPerYear !== lt.daysPerYear) return true
  if (override.isApprovalRequired !== lt.isApprovalRequired) return true
  if (override.shortestLeaveIntervalEnum !== lt.shortestLeaveIntervalEnum) return true
  return false
}

// ── Override form ─────────────────────────────────────────────────────────────

const OverrideForm = ({ lt, locationId, minLeaveDuration, onSave, onCancel, readOnly }: OverrideFormProps): ReactElement => {
  const { formatMessage } = useIntl()
  const existing = lt.overrides.find((o) => o.locationId === locationId)
  const isPTO = isPTOName(lt.name)
  const [unlimited, setUnlimited] = useState(existing?.hasUnlimitedDays ?? lt.hasUnlimitedDays)
  const rawDays = existing?.daysPerYear ?? lt.daysPerYear
  const [days, setDays] = useState<number | null>(rawDays)
  // Reads the seeded value, never the edited one: this states a fact about the jurisdiction, so
  // typing a 0 must not make it claim the law says 0.
  const hasNoStatutoryMinimum = !lt.hasUnlimitedDays && lt.daysPerYear === 0 && (isPTO || isSickLeaveName(lt.name))
  const [approval, setApproval] = useState(existing?.isApprovalRequired ?? lt.isApprovalRequired)
  const [intervalVal, setIntervalVal] = useState(existing?.shortestLeaveIntervalEnum ?? lt.shortestLeaveIntervalEnum)

  const save = () => {
    const finalDays = days ?? lt.daysPerYear
    onSave({
      locationId,
      locationName: existing?.locationName ?? locationId,
      hasUnlimitedDays: unlimited,
      daysPerYear: unlimited ? 0 : finalDays,
      isApprovalRequired: approval,
      allowHalfDays: intervalVal !== 'FULL-DAY',
      shortestLeaveIntervalEnum: intervalVal,
    })
  }

  return (
    <div className="vt-override-form">
      <div className="vt-override-fields">
        <div>
          <div className="vt-field-label">
            <IntlMessages id="onboarding.setup.leaveTypes.days" />
            {hasNoStatutoryMinimum && (
              <Tooltip title={<IntlMessages id="onboarding.setup.leaveTypes.noStatutoryMinimum" />}>
                <PiInfo size={13} style={{ color: '#8c8c8c', cursor: 'help', marginLeft: 4, verticalAlign: 'middle' }} />
              </Tooltip>
            )}
          </div>
          <div className="vt-override-field-box">
            <InputNumber
              min={0}
              max={365}
              value={days}
              onChange={(v) => setDays(v)}
              onBlur={() => {
                if (days === null) setDays(lt.daysPerYear)
              }}
              variant="borderless"
              disabled={unlimited || readOnly}
              style={{ width: '100%' }}
            />
          </div>
          <Checkbox checked={unlimited} onChange={(e) => setUnlimited(e.target.checked)} disabled={readOnly} style={{ marginTop: 6, fontSize: 12 }}>
            <IntlMessages id="onboarding.setup.leaveTypes.unlimited" />
          </Checkbox>
        </div>
        <div>
          <div className="vt-field-label">
            <IntlMessages id="onboarding.setup.leaveTypes.minDurationLabel" />
          </div>
          <div className="vt-override-field-box">
            <Select value={intervalVal} onChange={(v) => setIntervalVal(v)} variant="borderless" disabled={readOnly} style={{ width: '100%' }}>
              {getAllowedIntervals(minLeaveDuration).map((iv) => (
                <Option key={iv} value={iv}>
                  {formatMessage({ id: INTERVAL_TO_I18N_KEY[iv] ?? INTERVAL_TO_I18N_KEY['FULL-DAY'] })}
                </Option>
              ))}
            </Select>
          </div>
        </div>
        <div>
          <div className="vt-field-label">
            <IntlMessages id="onboarding.setup.approval" />
          </div>
          <div className="vt-override-field-box vt-override-field-box--switch">
            <Switch
              checked={approval}
              checkedChildren={formatMessage({ id: 'app.yes' })}
              unCheckedChildren={formatMessage({ id: 'app.no' })}
              onChange={(v) => setApproval(v)}
              disabled={readOnly}
            />
          </div>
        </div>
      </div>
      {!readOnly && (
        <Flex gap={8} justify="flex-end" style={{ marginTop: 12 }}>
          <Button size="small" onClick={onCancel}>
            <IntlMessages id="app.cancel" />
          </Button>
          <Button size="small" type="primary" onClick={save}>
            <IntlMessages id="app.save" />
          </Button>
        </Flex>
      )}
    </div>
  )
}

// ── Add leave type form ───────────────────────────────────────────────────────

const AddLeaveTypeForm = ({ existingCount, existingNames, locations, minLeaveDuration, onAdd, onCancel }: AddLeaveTypeFormProps): ReactElement => {
  const { formatMessage } = useIntl()
  const [name, setName] = useState('')
  const normalizedName = name.trim().toLowerCase()
  const isDuplicate = normalizedName !== '' && existingNames.some((n) => n.trim().toLowerCase() === normalizedName)
  const [color, setColor] = useState(LEAVE_TYPE_COLORS[existingCount % LEAVE_TYPE_COLORS.length])
  const [unlimited, setUnlimited] = useState(false)
  const [days, setDays] = useState(25)
  const [approval, setApproval] = useState(true)
  const [intervalVal, setIntervalVal] = useState('FULL-DAY')

  const handleAdd = () => {
    if (!name.trim()) return
    onAdd({
      id: crypto.randomUUID(),
      name: name.trim(),
      color,
      isActive: true,
      isFromDefault: false,
      hasUnlimitedDays: unlimited,
      daysPerYear: days,
      isApprovalRequired: approval,
      allowHalfDays: intervalVal !== 'FULL-DAY',
      shortestLeaveIntervalEnum: intervalVal,
      appliesToAll: true,
      appliesTo: locations.map((l) => l.id),
      overrides: [],
    })
  }

  return (
    <div className="vt-lt-add-form">
      <Space direction="vertical" style={{ width: '100%' }} size={16}>
        <div>
          <div className="vt-field-label">
            <IntlMessages id="app.name" />
          </div>
          <Input size="large" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Parental Leave" autoFocus status={isDuplicate ? 'error' : ''} />
          {isDuplicate && (
            <Text type="danger" style={{ fontSize: 12 }}>
              <IntlMessages id="onboarding.setup.leaveTypes.nameDuplicate" />
            </Text>
          )}
        </div>

        <div>
          <div className="vt-field-label">
            <IntlMessages id="app.color" />
          </div>
          <div className="vt-color-dots">
            {LEAVE_TYPE_COLORS.map((c) => (
              <button
                key={c}
                type="button"
                className={`vt-color-dot${c === color ? ' vt-color-dot--selected' : ''}`}
                style={{ backgroundColor: c, border: 'none', padding: 0 }}
                aria-label={`${formatMessage({ id: 'app.color' })}: ${c}`}
                aria-pressed={c === color}
                onClick={() => setColor(c)}
              />
            ))}
          </div>
        </div>

        <Flex gap={16} align="flex-start">
          <div style={{ flex: 1 }}>
            <div className="vt-field-label">
              <IntlMessages id="onboarding.setup.leaveTypes.days" />
            </div>
            <InputNumber size="large" min={0} max={365} value={days} onChange={(v) => setDays(v ?? 25)} disabled={unlimited} style={{ width: '100%' }} />
            <Checkbox checked={unlimited} onChange={(e) => setUnlimited(e.target.checked)} style={{ marginTop: 8, fontSize: 12 }}>
              <IntlMessages id="onboarding.setup.leaveTypes.unlimited" />
            </Checkbox>
          </div>
          <div style={{ flex: 1 }}>
            <div className="vt-field-label">
              <IntlMessages id="onboarding.setup.leaveTypes.minDurationLabel" />
            </div>
            <Select size="large" style={{ width: '100%' }} value={intervalVal} onChange={setIntervalVal}>
              {getAllowedIntervals(minLeaveDuration).map((iv) => (
                <Option key={iv} value={iv}>
                  {formatMessage({ id: INTERVAL_TO_I18N_KEY[iv] ?? INTERVAL_TO_I18N_KEY['FULL-DAY'] })}
                </Option>
              ))}
            </Select>
          </div>
        </Flex>

        <Divider style={{ margin: '4px 0' }} />

        <Flex align="center" justify="space-between" gap={16}>
          <div>
            <Text strong style={{ fontSize: 14, display: 'block' }}>
              <IntlMessages id="onboarding.setup.approval" />
            </Text>
            <Text type="secondary" style={{ fontSize: 12 }}>
              <IntlMessages id="onboarding.setup.leaveTypes.approvalDescription" />
            </Text>
          </div>
          <Switch checked={approval} checkedChildren={formatMessage({ id: 'app.yes' })} unCheckedChildren={formatMessage({ id: 'app.no' })} onChange={setApproval} />
        </Flex>

        <Flex gap={8} justify="flex-end">
          <Button onClick={onCancel}>
            <IntlMessages id="app.cancel" />
          </Button>
          <Button type="primary" disabled={!name.trim() || isDuplicate} onClick={handleAdd}>
            <IntlMessages id="onboarding.setup.button.add" />
          </Button>
        </Flex>
      </Space>
    </div>
  )
}

// ── Leave type row ────────────────────────────────────────────────────────────

const LeaveTypeRow = ({ lt, locations, minLeaveDuration, onChange, onDelete, readOnly, locationsReadOnly, isExpanded, onExpand }: LeaveTypeRowProps): ReactElement => {
  const { formatMessage } = useIntl()
  const [expandMode, setExpandMode] = useState<'none' | 'edit' | 'bylocation'>('none')

  useEffect(() => {
    if (!isExpanded) {
      setExpandMode('none')
      setEditState(null)
    }
  }, [isExpanded])
  const [customizingLocationId, setCustomizingLocationId] = useState<string | null>(null)
  const [editState, setEditState] = useState<EditState | null>(null)

  const overrideCount = lt.overrides.filter((o) => isOverrideCustomized(lt, o)).length

  const toggleMode = (mode: 'edit' | 'bylocation') => {
    if (mode === 'edit') {
      if (expandMode === 'edit') {
        setExpandMode('none')
        setEditState(null)
      } else {
        onExpand(lt.id)
        setExpandMode('edit')
        setEditState({
          name: lt.name,
          color: lt.color,
          hasUnlimitedDays: lt.hasUnlimitedDays,
          daysPerYear: lt.daysPerYear,
          isApprovalRequired: lt.isApprovalRequired,
          shortestLeaveIntervalEnum: lt.shortestLeaveIntervalEnum,
        })
      }
      setCustomizingLocationId(null)
    } else {
      if (expandMode !== mode) onExpand(lt.id)
      setExpandMode((prev) => (prev === mode ? 'none' : mode))
    }
  }

  const handleSaveEdit = () => {
    if (!editState) return
    const finalDays = editState.daysPerYear ?? lt.daysPerYear
    onChange({
      ...lt,
      name: editState.name.trim(),
      color: editState.color,
      hasUnlimitedDays: editState.hasUnlimitedDays,
      daysPerYear: editState.hasUnlimitedDays ? 0 : finalDays,
      isApprovalRequired: editState.isApprovalRequired,
      shortestLeaveIntervalEnum: editState.shortestLeaveIntervalEnum,
      allowHalfDays: editState.shortestLeaveIntervalEnum !== 'FULL-DAY',
      overrides: lt.overrides.map((o) => ({
        ...o,
        hasUnlimitedDays: editState.hasUnlimitedDays,
        daysPerYear: editState.hasUnlimitedDays ? 0 : finalDays,
        isApprovalRequired: editState.isApprovalRequired,
        allowHalfDays: editState.shortestLeaveIntervalEnum !== 'FULL-DAY',
        shortestLeaveIntervalEnum: editState.shortestLeaveIntervalEnum,
      })),
    })
    setExpandMode('none')
    setEditState(null)
  }

  const handleCancelEdit = () => {
    setExpandMode('none')
    setEditState(null)
  }

  const handleOverrideSave = (override: ILeaveTypeOverride) => {
    const withName = { ...override, locationName: locations.find((l) => l.id === override.locationId)?.name ?? override.locationId }
    const next = lt.overrides.filter((o) => o.locationId !== override.locationId).concat(withName)
    onChange({ ...lt, overrides: next })
    setCustomizingLocationId(null)
  }

  const enabledCount = lt.appliesToAll ? locations.length : lt.appliesTo.filter((id) => locations.some((l) => l.id === id)).length

  const toggleLocation = (locationId: string, enable: boolean) => {
    if (!enable && customizingLocationId === locationId) {
      setCustomizingLocationId(null)
    }
    const currentEnabled = lt.appliesToAll ? locations.map((l) => l.id) : lt.appliesTo
    const next = enable ? [...currentEnabled.filter((id) => id !== locationId), locationId] : currentEnabled.filter((id) => id !== locationId)
    const allSelected = locations.every((l) => next.includes(l.id))
    onChange({ ...lt, appliesTo: next, appliesToAll: allSelected })
  }

  return (
    <div className={`vt-leave-type-item${!lt.isActive ? ' vt-leave-type-item--inactive' : ''}`} style={{ borderLeftColor: lt.color }}>
      <div className="vt-lt-header">
        <Text className="vt-lt-name">{lt.name}</Text>
        <div className="vt-lt-meta">
          {overrideCount > 0 && (
            <span className="vt-lt-override-badge">
              {overrideCount === 1
                ? formatMessage({ id: 'onboarding.setup.leaveTypes.override.one' })
                : formatMessage({ id: 'onboarding.setup.leaveTypes.override.many' }, { count: overrideCount })}
            </span>
          )}
          <Text type="secondary" style={{ fontSize: 12 }}>
            {(() => {
              if (lt.appliesToAll) {
                return locations.length === 1 ? formatMessage({ id: 'onboarding.setup.summary.locations.one' }) : formatMessage({ id: 'onboarding.setup.leaveTypes.allLocations' })
              }
              const count = lt.appliesTo.filter((id) => locations.some((l) => l.id === id)).length
              return count === 1 ? formatMessage({ id: 'onboarding.setup.summary.locations.one' }) : formatMessage({ id: 'onboarding.setup.summary.locations.many' }, { count })
            })()}
          </Text>
        </div>
        <div className="vt-lt-actions">
          <Button
            size="small"
            onClick={() => toggleMode('bylocation')}
            icon={<PiMapPin size={11} />}
            style={{
              fontSize: 12,
              color: expandMode === 'bylocation' ? undefined : '#8c8c8c',
              borderColor: expandMode === 'bylocation' ? undefined : '#d9d9d9',
            }}
            type={expandMode === 'bylocation' ? 'primary' : 'default'}
            ghost={expandMode === 'bylocation'}
          >
            <IntlMessages id="onboarding.setup.leaveTypes.byLocation" />
          </Button>
          {!readOnly && (
            <>
              <Button type="link" size="small" onClick={() => toggleMode('edit')} style={{ fontSize: 12 }}>
                <IntlMessages id="app.edit" />
              </Button>
            </>
          )}
        </div>
      </div>

      {expandMode === 'edit' && editState && (
        <div className="vt-lt-edit-form">
          <Flex justify="space-between" align="center" style={{ marginBottom: 12 }}>
            <span className="vt-edit-form-title" style={{ marginBottom: 0 }}>
              <IntlMessages id="onboarding.setup.leaveTypes.editTitle" />
            </span>
            {!isPTOName(lt.name) && !isSickLeaveName(lt.name) && (
              <Button type="text" danger size="small" icon={<PiTrash size={13} />} onClick={() => onDelete(lt.id)}>
                <IntlMessages id="app.delete" />
              </Button>
            )}
          </Flex>
          <Space direction="vertical" style={{ width: '100%' }} size={16}>
            <div>
              <div className="vt-field-label">
                <IntlMessages id="app.name" />
              </div>
              <Input
                size="large"
                value={editState.name}
                onChange={(e) => setEditState({ ...editState, name: e.target.value })}
                status={editState.name.trim() === '' ? 'error' : ''}
              />
              {editState.name.trim() === '' && (
                <Text type="danger" style={{ fontSize: 12 }}>
                  <IntlMessages id="onboarding.setup.leaveTypes.nameRequired" />
                </Text>
              )}
            </div>

            <div>
              <div className="vt-field-label">
                <IntlMessages id="app.color" />
              </div>
              <div className="vt-color-dots">
                {LEAVE_TYPE_COLORS.map((c) => (
                  <button
                    key={c}
                    type="button"
                    className={`vt-color-dot${c === editState.color ? ' vt-color-dot--selected' : ''}`}
                    style={{ backgroundColor: c, border: 'none', padding: 0 }}
                    aria-label={`${formatMessage({ id: 'app.color' })}: ${c}`}
                    aria-pressed={c === editState.color}
                    onClick={() => setEditState({ ...editState, color: c })}
                  />
                ))}
              </div>
            </div>

            <Flex gap={16} align="flex-start">
              <div style={{ flex: 1 }}>
                <div className="vt-field-label">
                  <IntlMessages id="onboarding.setup.leaveTypes.days" />
                </div>
                <InputNumber
                  size="large"
                  min={0}
                  max={365}
                  value={editState.daysPerYear}
                  onChange={(v) => setEditState({ ...editState, daysPerYear: v })}
                  onBlur={() => {
                    if (editState.daysPerYear === null) setEditState({ ...editState, daysPerYear: lt.daysPerYear })
                  }}
                  disabled={editState.hasUnlimitedDays}
                  style={{ width: '100%' }}
                />
                <Checkbox
                  checked={editState.hasUnlimitedDays}
                  onChange={(e) => setEditState({ ...editState, hasUnlimitedDays: e.target.checked })}
                  style={{ marginTop: 8, fontSize: 12 }}
                >
                  <IntlMessages id="onboarding.setup.leaveTypes.unlimited" />
                </Checkbox>
              </div>
              <div style={{ flex: 1 }}>
                <div className="vt-field-label">
                  <IntlMessages id="onboarding.setup.leaveTypes.minDurationLabel" />
                </div>
                <Select
                  size="large"
                  style={{ width: '100%' }}
                  value={editState.shortestLeaveIntervalEnum}
                  onChange={(v) => setEditState({ ...editState, shortestLeaveIntervalEnum: v })}
                >
                  {getAllowedIntervals(minLeaveDuration).map((iv) => (
                    <Option key={iv} value={iv}>
                      {formatMessage({ id: INTERVAL_TO_I18N_KEY[iv] ?? INTERVAL_TO_I18N_KEY['FULL-DAY'] })}
                    </Option>
                  ))}
                </Select>
              </div>
            </Flex>

            <Divider style={{ margin: '4px 0' }} />

            <Flex align="center" justify="space-between" gap={16}>
              <div>
                <Text strong style={{ fontSize: 14, display: 'block' }}>
                  <IntlMessages id="onboarding.setup.approval" />
                </Text>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  <IntlMessages id="onboarding.setup.leaveTypes.approvalDescription" />
                </Text>
              </div>
              <Switch
                checked={editState.isApprovalRequired}
                checkedChildren={formatMessage({ id: 'app.yes' })}
                unCheckedChildren={formatMessage({ id: 'app.no' })}
                onChange={(v) => setEditState({ ...editState, isApprovalRequired: v })}
              />
            </Flex>

            <Flex gap={8} justify="flex-end">
              <Button onClick={handleCancelEdit}>
                <IntlMessages id="app.cancel" />
              </Button>
              <Button type="primary" onClick={handleSaveEdit} disabled={!editState.name.trim()}>
                <IntlMessages id="app.save" />
              </Button>
            </Flex>
          </Space>
        </div>
      )}

      {expandMode === 'bylocation' && (
        <div className="vt-lt-location-panel">
          <div className="vt-panel-section-label" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <IntlMessages id="onboarding.setup.leaveTypes.policyPerLocation" />
            <Tooltip title={<IntlMessages id="onboarding.setup.leaveTypes.policyPerLocationTooltip" />}>
              <PiInfo size={14} style={{ color: '#8c8c8c', cursor: 'help', flexShrink: 0 }} />
            </Tooltip>
          </div>
          <div className="vt-policy-rows">
            {locations.map((loc) => {
              const isEnabled = lt.appliesToAll || lt.appliesTo.includes(loc.id)
              const isLastActive = isEnabled && enabledCount === 1
              const override = lt.overrides.find((o) => o.locationId === loc.id)
              const isCustomizing = customizingLocationId === loc.id
              return (
                <div key={loc.id}>
                  <div
                    className={`vt-policy-row${!isEnabled ? ' vt-policy-row--disabled' : ''}`}
                    style={{ cursor: readOnly || !isEnabled ? 'default' : 'pointer' }}
                    onClick={readOnly || !isEnabled ? undefined : () => setCustomizingLocationId(isCustomizing ? null : loc.id)}
                  >
                    <div onClick={(e) => e.stopPropagation()}>
                      <Switch size="small" checked={isEnabled} disabled={isLastActive || readOnly || locationsReadOnly} onChange={(v) => toggleLocation(loc.id, v)} />
                    </div>
                    <PiMapPin size={12} style={{ color: '#8c8c8c', flexShrink: 0 }} />
                    <Text className="vt-policy-row-name">{loc.name}</Text>
                    {isEnabled && (
                      <Text className="vt-policy-row-meta">
                        {override && isOverrideCustomized(lt, override) && `${formatMessage({ id: 'onboarding.setup.leaveTypes.policyCustom' })} - `}
                        {policyLabel(lt, override)}
                      </Text>
                    )}
                    {isEnabled && !readOnly && (
                      <Button
                        size="small"
                        style={{
                          fontSize: 12,
                          color: isCustomizing ? undefined : '#8c8c8c',
                          borderColor: isCustomizing ? undefined : '#d9d9d9',
                        }}
                        type={isCustomizing ? 'primary' : 'default'}
                        ghost={isCustomizing}
                        onClick={(e) => {
                          e.stopPropagation()
                          setCustomizingLocationId(isCustomizing ? null : loc.id)
                        }}
                      >
                        <IntlMessages id="onboarding.setup.leaveTypes.customize" />
                      </Button>
                    )}
                  </div>
                  {isCustomizing && isEnabled && (
                    <OverrideForm
                      lt={lt}
                      locationId={loc.id}
                      minLeaveDuration={minLeaveDuration}
                      onSave={handleOverrideSave}
                      onCancel={() => setCustomizingLocationId(null)}
                      readOnly={readOnly}
                    />
                  )}
                </div>
              )
            })}
          </div>
        </div>
      )}
    </div>
  )
}

// ── Main step ─────────────────────────────────────────────────────────────────

const LT_PRIORITY = ['paid time off', 'sick leave', 'working remotely']
const sortLeaveTypesByPriority = (lts: IOnboardingLeaveType[]): IOnboardingLeaveType[] =>
  [...lts].sort((a, b) => {
    const ai = LT_PRIORITY.indexOf(a.name.toLowerCase())
    const bi = LT_PRIORITY.indexOf(b.name.toLowerCase())
    if (ai !== -1 && bi !== -1) return ai - bi
    if (ai !== -1) return -1
    if (bi !== -1) return 1
    return 0
  })

const LeaveTypesStep = ({
  leaveTypes,
  leaveTypesInitialized,
  signupLocationId,
  locations,
  leaveTypeLimit,
  minLeaveDuration,
  onUpdate,
  onInitialized,
  onConfirm,
  onSkip,
  onNext,
  readOnly,
  planLimitExceeded,
}: LeaveTypesStepProps): ReactElement => {
  const { formatMessage } = useIntl()
  const [loading, setLoading] = useState(false)
  const [showAddForm, setShowAddForm] = useState(false)
  const [activeLeaveTypeId, setActiveLeaveTypeId] = useState<string | null>(null)
  const { data: apiData, isError: isApiError } = useLeaveTypesApi()
  const [getLeaveTypeData] = useManualQuery<WizardLeaveTypeApiResult>(getWizardLeaveTypeData)

  useEffect(() => {
    if (leaveTypesInitialized || leaveTypes.length > 0 || !signupLocationId) return
    if (apiData === undefined && !isApiError) return

    setLoading(true)

    getLeaveTypeData({ variables: { locationId: signupLocationId } })
      .then((response) => {
        if (!response.data || response.error) throw response.error
        const data = response.data
        const policies = data.getLocation?.leavePolicies ?? []
        const policyMap = new Map(policies.map((p) => [p.leaveType.id, p]))
        const apiSuggestions = apiData ? getApiSuggestionsForLocations(apiData, locations, { signupLocationId }) : []
        const apiSuggestionsAll = apiData ? getApiSuggestionsForLocations(apiData, locations, { onlyDefault: false, signupLocationId }) : []

        const mapped: IOnboardingLeaveType[] = data.getLeaveTypeList.map((lt, idx) => {
          const policy = policyMap.get(lt.id)
          const apiMatch = matchToApiSuggestion(lt.name, apiSuggestionsAll)

          return {
            id: lt.id,
            name: DEFAULT_NAME_MAP[lt.name] ?? lt.name,
            color: lt.color ?? LEAVE_TYPE_COLORS[idx % LEAVE_TYPE_COLORS.length],
            isActive: true,
            isFromDefault: true,
            hasUnlimitedDays: apiMatch?.hasUnlimitedDays ?? policy?.hasUnlimitedDays ?? false,
            daysPerYear: apiMatch?.hasUnlimitedDays ? 0 : (apiMatch?.daysPerYear ?? policy?.daysPerYear ?? 0),
            isApprovalRequired: apiMatch?.isApprovalRequired ?? policy?.isApprovalRequired ?? true,
            allowHalfDays: apiMatch ? apiMatch.allowHalfDays : (policy?.allowHalfDays ?? false),
            shortestLeaveIntervalEnum: (() => {
              const interval = normalizeIntervalEnum(apiMatch?.shortestLeaveIntervalEnum ?? policy?.shortestLeaveInterval)
              const allowed = getAllowedIntervals(minLeaveDuration)
              return allowed.includes(interval) ? interval : normalizeIntervalEnum(minLeaveDuration)
            })(),
            appliesToAll: true,
            appliesTo: locations.map((l) => l.id),
            overrides: [],
          }
        })

        // Add isDefault:true API suggestions not already represented by DB leave types
        const dbNames = new Set(mapped.map((lt) => lt.name.toLowerCase()))
        const apiExtras: IOnboardingLeaveType[] = apiSuggestions
          .filter((s) => !dbNames.has(s.name.toLowerCase()))
          .map((s, idx) => {
            return {
              id: crypto.randomUUID(),
              name: s.name,
              color: LEAVE_TYPE_COLORS[(mapped.length + idx) % LEAVE_TYPE_COLORS.length],
              isActive: true,
              isFromDefault: false,
              hasUnlimitedDays: s.hasUnlimitedDays,
              daysPerYear: s.hasUnlimitedDays ? 0 : s.daysPerYear,
              isApprovalRequired: s.isApprovalRequired,
              allowHalfDays: s.allowHalfDays,
              shortestLeaveIntervalEnum: (() => {
                const interval = normalizeIntervalEnum(s.shortestLeaveIntervalEnum)
                const allowed = getAllowedIntervals(minLeaveDuration)
                return allowed.includes(interval) ? interval : normalizeIntervalEnum(minLeaveDuration)
              })(),
              appliesToAll: true,
              appliesTo: locations.map((l) => l.id),
              overrides: [],
            }
          })

        onInitialized(sortLeaveTypesByPriority([...mapped, ...apiExtras]))
      })
      .catch((err) => console.error('[LeaveTypesStep] Failed to load leave type data:', err))
      .finally(() => setLoading(false))
  }, [leaveTypesInitialized, signupLocationId, apiData, isApiError])

  useEffect(() => {
    if (leaveTypes.length === 0 || leaveTypes.length <= leaveTypeLimit) return
    const pto = leaveTypes.find((lt) => lt.name.toLowerCase().includes('paid time off')) ?? leaveTypes[0]
    const remaining = [pto, ...leaveTypes.filter((lt) => lt.id !== pto.id)].slice(0, leaveTypeLimit)
    const needsAppliesToAll = leaveTypeLimit === 1 && !remaining[0].appliesToAll
    if (!needsAppliesToAll && leaveTypes.length === remaining.length) return
    onUpdate(needsAppliesToAll ? [{ ...remaining[0], appliesToAll: true }] : remaining)
  }, [leaveTypeLimit, leaveTypes.length, leaveTypes[0]?.appliesToAll])

  const handleChange = (updated: IOnboardingLeaveType) => {
    onUpdate(leaveTypes.map((lt) => (lt.id === updated.id ? updated : lt)))
  }

  const handleDelete = (id: string) => {
    onUpdate(leaveTypes.filter((lt) => lt.id !== id))
  }

  const handleAdd = (lt: IOnboardingLeaveType) => {
    onUpdate([...leaveTypes, lt])
    setShowAddForm(false)
  }

  const activeCount = leaveTypes.filter((lt) => lt.isActive).length
  const atLimit = leaveTypes.length >= leaveTypeLimit
  const confirmLabel =
    activeCount > 1
      ? formatMessage({ id: 'onboarding.setup.leaveTypes.confirmButton.many' }, { count: activeCount })
      : formatMessage({ id: 'onboarding.setup.leaveTypes.confirmButton.looksGood' })

  const isWaitingForApi = !leaveTypesInitialized && leaveTypes.length === 0 && apiData === undefined && !isApiError

  if (loading || isWaitingForApi) {
    return (
      <Flex justify="center" style={{ padding: '60px 0' }}>
        <Spin />
      </Flex>
    )
  }

  return (
    <Space direction="vertical" style={{ width: '100%' }} size={16}>
      {readOnly && (
        <div className="vt-readonly-notice">
          <IntlMessages
            id="onboarding.setup.readOnly.message"
            values={{
              link: (
                <Link to={FrontendUrls.leaveTypes}>
                  <IntlMessages id="app.leaveTypes" />
                </Link>
              ),
            }}
          />
        </div>
      )}
      <div>
        <Typography.Title level={4} className="vt-onboarding-title">
          <IntlMessages id="onboarding.setup.leaveTypes.title" />
        </Typography.Title>
        <Text className="vt-onboarding-subtitle">
          <IntlMessages id="onboarding.setup.leaveTypes.subtitle" />
        </Text>
      </div>

      <div>
        {leaveTypes.length > 0 && (
          <div className="vt-leave-type-list">
            {leaveTypes.map((lt) => (
              <LeaveTypeRow
                key={lt.id}
                lt={lt}
                locations={locations}
                minLeaveDuration={minLeaveDuration}
                onChange={handleChange}
                onDelete={handleDelete}
                readOnly={readOnly}
                locationsReadOnly={leaveTypeLimit === 1}
                isExpanded={activeLeaveTypeId === lt.id}
                onExpand={setActiveLeaveTypeId}
              />
            ))}
          </div>
        )}

        {!readOnly && !atLimit && !showAddForm && (
          <button className="vt-add-leave-type-btn" onClick={() => setShowAddForm(true)}>
            <IntlMessages id="onboarding.setup.leaveTypes.addAnother" />
          </button>
        )}

        {showAddForm && (
          <AddLeaveTypeForm
            existingCount={leaveTypes.length}
            existingNames={leaveTypes.map((lt) => lt.name)}
            locations={locations}
            minLeaveDuration={minLeaveDuration}
            onAdd={handleAdd}
            onCancel={() => setShowAddForm(false)}
          />
        )}
      </div>

      {readOnly && onNext && (
        <Button block size="large" onClick={onNext}>
          <IntlMessages id="onboarding.setup.button.next" />
        </Button>
      )}

      {!readOnly && (
        <Space direction="vertical" style={{ width: '100%' }} size={8}>
          <Button type="primary" block size="large" className="vt-onboarding-confirm-btn" onClick={onConfirm} disabled={activeCount === 0 || planLimitExceeded}>
            {confirmLabel}
          </Button>
          <div className="vt-onboarding-skip">
            <Typography.Link onClick={onSkip}>
              <IntlMessages id="onboarding.setup.skipForNow" />
            </Typography.Link>
          </div>
        </Space>
      )}
    </Space>
  )
}

export default LeaveTypesStep
